import React, { useEffect, useState } from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';
import { FeedbackInput } from './FeedbackInput';

/**
 * RegenerateWithFeedbackModal
 *
 * A focused modal for regeneration actions (per-ad OR campaign-level)
 * that lets the user type free-form feedback before the new version is
 * generated. The feedback is sent to the backend as `user_feedback` and
 * injected into the AI prompt so the regenerated output incorporates the
 * requested changes.
 *
 * Themed: uses `useColorTheme()` so the header + confirm button match the
 * user's saved theme (green / purple / blue) from the database. No manual
 * prop drilling needed.
 *
 * Falls back gracefully — empty textarea = same behaviour as before
 * (no behavioural change for users who just want to regenerate as-is).
 */
const RegenerateWithFeedbackModal = ({
  isOpen,
  onClose,
  onConfirm,           // (feedback: string) => void | Promise<void>
  title = '✨ Regenerate',
  description,         // string — credit/persona summary to show above the textarea
  confirmText = 'Regenerate',
  cancelText = 'Cancel',
  isProcessing = false,
  testIdPrefix = 'regenerate-feedback',
}) => {
  const { currentColors } = useColorTheme();
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (isOpen) setFeedback('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    await onConfirm(feedback.trim());
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
    }
  };

  // Pull themed gradients from the context (DB-backed user choice)
  const headerGradient = `bg-gradient-to-r ${currentColors?.buttonGradient || 'from-violet-500 to-fuchsia-500'}`;
  const buttonGradient = `bg-gradient-to-r ${currentColors?.buttonGradient || 'from-violet-600 to-fuchsia-600'} hover:${currentColors?.hoverButtonGradient || 'from-violet-700 to-fuchsia-700'}`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isProcessing) onClose?.();
      }}
      data-testid={`${testIdPrefix}-modal`}
    >
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header — themed */}
        <div className={`${headerGradient} px-6 py-4`}>
          <h3 className="text-lg font-semibold text-white" data-testid={`${testIdPrefix}-title`}>
            {title}
          </h3>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {description && (
            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">
              {description}
            </p>
          )}
          <FeedbackInput
            value={feedback}
            onChange={setFeedback}
            label="Anything to fix or change?"
            rows={5}
            disabled={isProcessing}
            testIdBase={testIdPrefix}
            helperText={
              <>
                Leave blank to regenerate without changes.{' '}
                <span className="hidden sm:inline">⌘/Ctrl+Enter to submit.</span>
              </>
            }
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-end gap-2 border-t border-slate-100 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-60"
            data-testid={`${testIdPrefix}-cancel`}
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isProcessing}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${buttonGradient} transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2`}
            data-testid={`${testIdPrefix}-confirm`}
          >
            {isProcessing && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegenerateWithFeedbackModal;
